import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import utils from '../../utils'
import { useCart } from '../../context/CartContext'
import paymentsService from '../../services/PaymentServices'

const MAX_RETRIES = 27; 
const RETRY_DELAY_MS = 3000;

function MpesaPaymentModal({ onPaymentInitiated }) {
    const navigate = useNavigate()
    const [phoneNumber, setPhoneNumber] = useState("")
    const [loading, setLoading] = useState(false)
    const { payment, resetCart } = useCart(); 

    const clearInput = () => {
        setPhoneNumber("")
    }

    const checkPaymentWithRetry = async (reference, loadingToast, attempt = 1) => {
        if (attempt > MAX_RETRIES) {
            toast.error("Payment verification timed out. Please check your M-Pesa messages.", { id: loadingToast });
            setLoading(false);
            return;
        }

        try {
            const res = await paymentsService.confirmPayment(reference);

            if (res.status === 200) {
                toast.success("Payment Confirmed!", { id: loadingToast });
                resetCart();
                navigate('/cart/checkout/complete');
                setLoading(false);
                const closeBtn = document.getElementById("mpesa-open-btn");
                if (closeBtn) closeBtn.click();

                if (onPaymentInitiated) onPaymentInitiated();
            } else {
                setTimeout(() => checkPaymentWithRetry(reference, loadingToast, attempt + 1), RETRY_DELAY_MS);
            }
        } catch (err) {
            if (err.response?.status === 202) {
                setTimeout(() => checkPaymentWithRetry(reference, loadingToast, attempt + 1), RETRY_DELAY_MS);
            } else if (err.response?.status === 400) {
                toast.error("Transaction failed or cancelled.", { id: loadingToast });
                setLoading(false);
            } else {
                setTimeout(() => checkPaymentWithRetry(reference, loadingToast, attempt + 1), RETRY_DELAY_MS);
            }
        }
    };

    async function submitHandler(e) {
        e.preventDefault()
        setLoading(true)
        let formattedPhone = phoneNumber.replace(/\s+/g, '').replace(/-/g, '')
        if (formattedPhone.startsWith('0')) {
            formattedPhone = '254' + formattedPhone.substring(1)
        } else if (formattedPhone.startsWith('+254')) {
            formattedPhone = formattedPhone.substring(1)
        }

        const data = {
            'phone_number': formattedPhone,
            'transaction_reference': payment?.transaction_reference
        }

        const loadingToast = toast.loading("Sending STK Push...");

        try {
            await paymentsService.mpesaSTK(data);
            toast.success("STK Push sent! Check your phone.", { id: loadingToast });

            const verifyToast = toast.loading("Verifying payment...");
            checkPaymentWithRetry(payment?.transaction_reference, verifyToast, 1);

        } catch (err) {
            console.error("STK Push error:", err);
            let errorMsg = "Failed to send STK Push. Please try again.";
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            }
            toast.error(errorMsg, { id: loadingToast });
            setLoading(false);
        }
    }

    return (
        <div>
            <label htmlFor="mpesa-modal" id='mpesa-open-btn' className="hidden">Mpesa Payment</label>
            <input type="checkbox" id="mpesa-modal" className="modal-toggle" />
            <div className="modal">
                <div className="modal-box w-11/12 max-w-sm">
                    <label
                        onClick={clearInput}
                        htmlFor="mpesa-modal"
                        id='mpesa-open-btn'
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</label>

                    <h3 className="font-bold text-lg text-center mb-1">Pay with M-Pesa</h3>
                    <p className="text-center text-gray-500 mb-2">
                        Amount: <span className="font-bold text-green-600">KES {utils.toCommas(parseInt(payment?.total ?? 0))}</span>
                    </p>
                    <p className="text-center text-sm text-gray-500 mb-4">
                        Keep your phone nearby and enter your PIN when the M-Pesa prompt appears.
                    </p>

                    <form onSubmit={submitHandler}>
                        <div className="form-control mb-4">
                            <label className="label">
                                <span className="label-text">M-Pesa Phone Number:</span>
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                autoComplete="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="2547XXXXXXXX"
                                required
                                className="input input-bordered w-full"
                            />
                            <label className="label">
                                <span className="label-text-alt text-gray-500">
                                    Enter the number registered to M-Pesa.
                                </span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-75"
                        >
                            {loading ? 'Processing...' : 'Initiate STK Push'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default MpesaPaymentModal